import { Component } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { HttpService } from 'src/app/http.service';

@Component({
  selector: 'app-withdraw',
  templateUrl: './withdraw.component.html',
  styleUrls: ['./withdraw.component.scss']
})
export class WithdrawComponent {

  public withdraws:any = [];
  public banks:any = [];
  public status:number = 2;
  public company_uuid:string;
  public is_loading:boolean = false;
  public allow_edit_balance:boolean = false;
  public trim_step:number = 1;
  public filter = { date_from: '', date_to: '', search: '' };
  public error = '';
  private requestId = 0;
  constructor(private http:HttpService, private ngbModal:NgbModal) {
    this.company_uuid = this.http.getStaticSelectedCompany();
    this.getSetting();
    this.getWithdraws();
    this.getBanks();
  }

  public async getSetting() {
    let rtn:any = await this.http.post("/admin/company/setting");
    this.allow_edit_balance = rtn && rtn.setting && rtn.setting.allow_edit_withdraw_balance === true;
    let step = rtn && rtn.setting ? parseFloat(rtn.setting.withdraw_trim_step) : 1;
    this.trim_step = (isNaN(step) || step <= 0) ? 1 : step;
  }

  public async getBanks() {
    let rtn:any = await this.http.post("/admin/bank/get", {usage: 'withdraw'});
    this.banks = rtn.banks;
  }

  public async getWithdraws(status:number = this.status):Promise<void> {
    if (this.filter.date_from && this.filter.date_to && this.filter.date_from > this.filter.date_to) {
      this.error = 'From date must be on or before To date.';
      return;
    }
    const request = ++this.requestId;
    this.is_loading = true;
    this.error = '';
    this.status = status;
    this.withdraws = [];
    let rtn:any = await this.http.post("/admin/withdraw/get", {status: this.status, filter: { ...this.filter }});
    if (request !== this.requestId) return;
    this.withdraws = Array.isArray(rtn) ? rtn : [];
    if (!Array.isArray(rtn)) this.error = rtn?.message || 'Unable to load withdrawals. Please retry.';
    // Preserve the player's originally requested amount as the editable ceiling.
    this.withdraws.forEach((w:any) => { w.max_amount = parseFloat(w.amount); });
    this.is_loading = false;
  }

  clearFilters() {
    this.filter = { date_from: '', date_to: '', search: '' };
    void this.getWithdraws();
  }

  // Trim the editable amount down to the nearest LOWER multiple of the tenant's
  // configured trim step, always landing STRICTLY BELOW the current value, e.g.
  // with step 1: 69.86 -> 69, 70.00 -> 69; with step 5: 79.30 -> 75, 80 -> 75.
  // The trimmed withdraw must stay strictly under the player's held credit so
  // that any withdraw charge never causes an insufficient-credit shortfall, so a
  // value already on a step boundary drops by a full step.
  trimAmount(withdraw:any) {
    let v = parseFloat(withdraw.amount);
    if (isNaN(v) || v <= 0) { withdraw.amount = 0; return; }
    let s = parseFloat(this.trim_step as any);
    if (isNaN(s) || s <= 0) { s = 1; }
    let result = Math.floor(v / s) * s;
    if (result >= v - 1e-9) { result -= s; }
    result = Math.round((result + Number.EPSILON) * 100) / 100;
    if (result < 0) { result = 0; }
    withdraw.amount = result;
  }

  // Clamp the edited amount so it never exceeds the requested value or drops <= 0.
  clampAmount(withdraw:any) {
    let v = parseFloat(withdraw.amount);
    if (isNaN(v) || v < 0) { v = 0; }
    if (v > withdraw.max_amount) { v = withdraw.max_amount; }
    withdraw.amount = v;
  }

  async updateWithdraw(withdraw:any) {

    // Re-entry / multi-click guard. The UPDATE button stays visible while the
    // POST is in flight (the approve is slow: base64 proof upload + bank/gateway
    // lookup + ledger writes), so impatient staff click it repeatedly and each
    // click used to fire a fresh /admin/withdraw/update — the exact cause of the
    // duplicate transaction_bank rows. Block any click while one is pending.
    if(withdraw._submitting) {
      return;
    }

    if(withdraw.status == 2) {
      this.http.toast("Please select status.", "error");
      return;
    }

    if(withdraw.status == 1 && !withdraw.bank_id) {
      this.http.toast("Please select bank.", "error");
      return;
    }

    if(withdraw.status == 1 && withdraw.bank_id && !withdraw.is_auto && !withdraw.file) {
      this.http.toast("Please upload proof of payment.", "error");
      return;
    }

    if(this.allow_edit_balance && withdraw.status == 1) {
      let amount = parseFloat(withdraw.amount);
      if(isNaN(amount) || amount <= 0) {
        this.http.toast("Withdraw amount must be greater than 0.", "error");
        return;
      }
      if(amount > withdraw.max_amount) {
        this.http.toast("Withdraw amount cannot exceed the requested amount.", "error");
        return;
      }
    }

    withdraw._submitting = true;
    let rtn:any;
    try {
      rtn = await this.http.post("/admin/withdraw/update", withdraw);
    } finally {
      withdraw._submitting = false;
    }

    if(rtn && rtn.status){
      this.getWithdraws();
    }

    if(rtn && rtn.message){
      this.http.toast(rtn.message, rtn.status ? "success" : "error");
    }
  }

  changeBank(withdraw:any) {
    withdraw.is_auto = this.banks.find(bank => bank.id == withdraw.bank_id).auto;
  }

  async uploadFile(event:any, withdraw:any) {
    let base64 = await this.http.fileChangeEvent(event);
    withdraw.file = base64;
  }
}
